import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TODAY } from '../utils/dates';
import { ArrowUp, Mic, MicOff } from 'lucide-react';
import css from './ChatPage.module.css';

// ── Framed Mylo icon ──────────────────────────────────────────────────────────
// Auburn dot uses var(--accent) so it rotates with the season

function MyloIcon({ size = 64 }) {
  const r    = Math.round(size * 0.22);   // corner radius
  const fs   = Math.round(size * 0.54);   // "m" font size
  const mY   = Math.round(size * 0.685);  // m baseline
  // Dot: 8px diameter at 64, 7px at 56, 4px at 28
  // Formula: size * 0.075 gives 4.8 / 4.2 / 2.1 — use as radius, min 2
  const dotR  = Math.max(2, +(size * 0.075).toFixed(1));
  const dotCx = +(size * 0.665).toFixed(1);
  const dotCy = +(size * 0.295).toFixed(1);
  return (
    <svg
      width={size} height={size} viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <rect
        width={size} height={size} rx={r}
        fill="var(--s2, #2a2420)"
        stroke="var(--b1, rgba(255,255,255,0.08))"
        strokeWidth="0.5"
      />
      <text
        x={size / 2} y={mY}
        textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize={fs}
        fontWeight="600"
        fill="var(--ink, #E8DDD0)"
      >m</text>
      {/* Auburn dot — hardcoded fill so it never goes dark */}
      <circle cx={dotCx} cy={dotCy} r={dotR} fill="var(--accent)" />
    </svg>
  );
}

// ── Date/time live stamp ──────────────────────────────────────────────────────
function DateStamp() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);
  const days   = ['sun','mon','tue','wed','thu','fri','sat'];
  const months = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
  const dow  = days[now.getDay()];
  const mon  = months[now.getMonth()];
  const day  = now.getDate();
  let   h    = now.getHours();
  const min  = String(now.getMinutes()).padStart(2, '0');
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return (
    <div className={css.dateStamp}>
      {dow}, {mon} {day} · {h}:{min} {ampm}
    </div>
  );
}

// ── Greeting with accent initial ──────────────────────────────────────────────
function GreetText({ name }) {
  const h     = new Date().getHours();
  const first = name?.split(' ')[0] || null;
  let pre;
  if (h >= 5  && h < 12) pre = 'morning';
  else if (h >= 12 && h < 17) pre = 'afternoon';
  else if (h >= 17 && h < 21) pre = 'evening';
  else pre = 'still up';
  if (!first) return <span>{pre}.</span>;
  const initial = first[0].toUpperCase();
  return (
    <span>
      {pre},{' '}
      <span className={css.accentInitial}>{initial}.</span>
    </span>
  );
}

// ── Starter chips ─────────────────────────────────────────────────────────────
const STATIC_STARTERS = [
  { lbl: '+ add a task',     pre: 'Add a task: ',             primary: true  },
  { lbl: "what's on today",  pre: "What's on my plate today?",primary: false },
  { lbl: 'log a thought',    pre: "I'm thinking ",            primary: false },
  { lbl: 'remind me to',     pre: 'Remind me to ',            primary: false },
  { lbl: 'check my goals',   pre: 'How are my goals looking?',primary: false },
];

// Build context-aware starters from live state
function buildStarters(st, sigs) {
  const starters = [];
  const workouts = st?.workouts || [];
  const tasks    = st?.tasks    || [];
  const income   = st?.income   || [];
  const TODAY    = new Date().toISOString().slice(0, 10);

  // Race proximity
  const raceDate = st?.raceDate;
  if (raceDate) {
    const days = Math.round((new Date(raceDate+'T12:00:00') - new Date(TODAY+'T12:00:00')) / 86400000);
    if (days >= 0 && days <= 21)
      starters.push({ lbl: `race in ${days}d — how's training?`, pre: `My race is in ${days} days — how is my training looking?`, primary: true });
  }

  // Last workout
  if (workouts.length > 0) {
    const lastDate = workouts[0].date;
    const daysAgo = Math.round((new Date(TODAY+'T12:00:00') - new Date(lastDate+'T12:00:00')) / 86400000);
    if (daysAgo >= 3)
      starters.push({ lbl: `log a workout`, pre: `Log a workout: `, primary: starters.length === 0 });
  }

  // Payday
  const nextPay = income.filter(i => i.nextDate).sort((a,b) => a.nextDate < b.nextDate ? -1 : 1)[0];
  if (nextPay?.nextDate) {
    const payDays = Math.round((new Date(nextPay.nextDate+'T12:00:00') - new Date(TODAY+'T12:00:00')) / 86400000);
    if (payDays <= 2 && payDays >= 0)
      starters.push({ lbl: `payday ${payDays === 0 ? 'today' : 'tomorrow'} — review bills`, pre: `Payday is ${payDays === 0 ? 'today' : 'tomorrow'} — what bills do I have coming up?`, primary: false });
  }

  // Open tasks
  const openToday = tasks.filter(t => !t.done && (t.bucket === 'today' || t.dueDate === TODAY));
  if (openToday.length > 0)
    starters.push({ lbl: `${openToday.length} task${openToday.length !== 1 ? 's' : ''} today`, pre: `What should I focus on from my tasks today?`, primary: starters.length === 0 });

  // Warn signals
  const topWarn = (sigs || []).find(s => s.lvl === 'warn');
  if (topWarn)
    starters.push({ lbl: 'any alerts?', pre: 'Do I have any alerts or things I should know about?', primary: false });

  // Fill to 5 with static starters
  for (const s of STATIC_STARTERS) {
    if (starters.length >= 5) break;
    if (!starters.find(x => x.pre === s.pre)) starters.push(s);
  }
  return starters.slice(0, 5);
}

// ── Tool execution — replaces <<<ACTION>>> parsing ───────────────────────────
// Called when Claude returns a tool_use block with a name + validated input.
// Returns a short confirmation label or null.
function executeToolCall(name, input, setSt, setTab, notify, TODAY) {
  const id = () => `t${Date.now()}${Math.random().toString(36).slice(2,6)}`;

  switch (name) {
    // ── Tasks ──────────────────────────────────────────────────────────────
    case 'add_task':
      setSt(p => ({
        ...p,
        tasks: [{ id: id(), text: input.text, bucket: input.bucket || 'today',
          priority: input.priority || 'medium', dueDate: input.dueDate || null,
          done: false, createdAt: TODAY, listId: input.listId || null,
        }, ...(p.tasks || [])],
      }));
      return `task added: ${input.text}`;

    case 'complete_task':
      setSt(p => {
        const lower = (input.text || '').toLowerCase();
        const match = (p.tasks || []).find(t => t.text?.toLowerCase().includes(lower));
        if (!match) return p;
        return { ...p, tasks: p.tasks.map(t => t.id === match.id
          ? { ...t, done: true, completedAt: TODAY } : t) };
      });
      return `completed: ${input.text}`;

    case 'update_task':
      setSt(p => {
        const lower = (input.text || '').toLowerCase();
        const match = (p.tasks || []).find(t => t.text?.toLowerCase().includes(lower));
        if (!match) return p;
        const updates = {};
        if (input.bucket) updates.bucket = input.bucket;
        if (input.priority) updates.priority = input.priority;
        if (input.dueDate) updates.dueDate = input.dueDate;
        return { ...p, tasks: p.tasks.map(t => t.id === match.id ? { ...t, ...updates } : t) };
      });
      return `updated: ${input.text}`;

    // ── Health ─────────────────────────────────────────────────────────────
    case 'log_workout':
      setSt(p => ({
        ...p,
        workouts: [{ id: id(), type: input.type, miles: input.miles || null,
          duration: input.duration || null, intensity: input.intensity || 'moderate',
          note: input.note || '', date: input.date || TODAY,
        }, ...(p.workouts || [])],
      }));
      return `logged ${input.type}${input.miles ? ` · ${input.miles}mi` : input.duration ? ` · ${input.duration}min` : ''}`;

    case 'check_in_habit':
      setSt(p => {
        const lower = (input.name || '').toLowerCase();
        const match = (p.habits || []).find(h => h.name?.toLowerCase().includes(lower));
        if (!match) return p;
        const today = { date: TODAY, done: true };
        return { ...p, habits: (p.habits||[]).map(h => h.id === match.id
          ? { ...h, log: [...(h.log||[]).filter(l=>l.date!==TODAY), today] } : h) };
      });
      return `checked in: ${input.name}`;

    // ── Finance ────────────────────────────────────────────────────────────
    case 'log_transaction':
      setSt(p => ({
        ...p,
        transactions: [{ id: id(), amount: input.amount, category: input.category,
          note: input.note || '', date: input.date || TODAY, source: 'coach',
        }, ...(p.transactions || [])],
      }));
      return `logged $${input.amount} · ${input.category}${input.note ? ` (${input.note})` : ''}`;

    case 'add_expense':
      setSt(p => ({
        ...p,
        finance: [{ id: id(), name: input.name, amount: input.amount,
          freq: input.freq, cat: input.cat || 'other', next: input.next || null,
        }, ...(p.finance || [])],
      }));
      return `added expense: ${input.name} $${input.amount}/${input.freq}`;

    // ── Goals ──────────────────────────────────────────────────────────────
    case 'add_goal':
      setSt(p => ({
        ...p,
        goals: [{ id: id(), title: input.title, cat: input.cat,
          target: input.target || '', targetDate: input.targetDate || null,
          note: input.note || '', progress: 0, createdAt: TODAY,
        }, ...(p.goals || [])],
      }));
      return `goal added: ${input.title}`;

    case 'update_goal_progress':
      setSt(p => {
        const lower = (input.title || '').toLowerCase();
        const match = (p.goals || []).find(g => g.title?.toLowerCase().includes(lower));
        if (!match) return p;
        return { ...p, goals: (p.goals||[]).map(g => g.id === match.id
          ? { ...g, progress: Math.min(100, Math.max(0, input.progress)) } : g) };
      });
      return `goal updated to ${input.progress}%`;

    // ── Vinyl ──────────────────────────────────────────────────────────────
    case 'add_vinyl':
      setSt(p => ({
        ...p,
        vinyl: [{ id: id(), artist: input.artist, album: input.album,
          vendor: input.vendor || '', eta: input.eta || null,
          price: input.price || null, status: 'ordered', delivered: false,
        }, ...(p.vinyl || [])],
      }));
      return `vinyl added: ${input.artist} — ${input.album}`;

    case 'mark_vinyl_delivered':
      setSt(p => {
        const lower = (input.artist || '').toLowerCase();
        const match = (p.vinyl || []).find(v => v.artist?.toLowerCase().includes(lower) && !v.delivered);
        if (!match) return p;
        return { ...p, vinyl: p.vinyl.map(v => v.id === match.id
          ? { ...v, delivered: true, status: 'collection', deliveredDate: TODAY } : v) };
      });
      return `marked delivered: ${input.artist}`;

    // ── Calendar ───────────────────────────────────────────────────────────
    case 'add_event':
      setSt(p => ({
        ...p,
        events: [{ id: id(), title: input.title, date: input.date,
          time: input.time || null, note: input.note || '',
        }, ...(p.events || [])],
      }));
      return `event added: ${input.title} on ${input.date}`;

    case 'add_reminder':
      setSt(p => ({
        ...p,
        reminders: [{ id: id(), message: input.message, date: input.date,
          time: input.time || '09:00', repeat: input.repeat || 'none',
          category: input.category || 'general', done: false, createdAt: TODAY,
        }, ...(p.reminders || [])],
      }));
      return `reminder set: ${input.message}`;

    // ── Memory & notes ─────────────────────────────────────────────────────
    case 'add_decision':
      setSt(p => ({
        ...p,
        decisions: [{
          id: `d${Date.now()}`,
          title: input.title,
          context: input.context || '',
          decision: input.decision || '',
          outcome: input.outcome || '',
          date: input.date || TODAY,
          reviewDate: input.review_date || null,
          domain: input.domain || 'general',
          status: 'open',
        }, ...(p.decisions || [])],
      }));
      return `decision logged: ${input.title}`;

    case 'update_decision':
      setSt(p => {
        const lower = (input.title || '').toLowerCase();
        const match = (p.decisions || []).find(d => d.title?.toLowerCase().includes(lower));
        if (!match) return p;
        const updates = {};
        if (input.outcome) updates.outcome = input.outcome;
        if (input.status)  updates.status  = input.status;
        return { ...p, decisions: (p.decisions||[]).map(d => d.id === match.id ? { ...d, ...updates } : d) };
      });
      return `decision updated: ${input.title}`;

    case 'add_memory':
      setSt(p => ({
        ...p,
        memories: [
          { key: input.key, cat: input.cat, text: input.text, createdAt: TODAY },
          ...(p.memories || []).filter(m => m.key !== input.key),
        ],
      }));
      return null; // silent — memory storage shouldn't show a card

    case 'capture_note':
      setSt(p => ({
        ...p,
        notes: [{ id: id(), text: input.text, tags: input.tags || [],
          createdAt: TODAY,
        }, ...(p.notes || [])],
      }));
      return `noted: ${(input.text || '').slice(0, 80)}`;

    case 'set_tab':
      if (input.tab && setTab) setTab(input.tab);
      return null; // silent navigation

    default:
      return null;
  }
}

// ── Verb label map ────────────────────────────────────────────────────────────
function verbFor(type) {
  return ({
    add_task:'created task',add_event:'created event',add_goal:'created goal',
    add_vinyl:'added vinyl',add_workout:'logged workout',add_reminder:'set reminder',
    add_finance:'added expense',add_memory:'remembered',create_habit:'created habit',
    complete_task:'completed task',update_goal_progress:'updated goal',
    mark_vinyl_delivered:'marked delivered',update_task:'updated task',
    update_event:'updated event',check_in_habit:'checked in',
    update_habit_target:'updated habit',delete_task:'deleted task',
    delete_event:'deleted event',delete_habit:'deleted habit',
    capture_note:'noted',log_payday:'logged payday',
    add_decision:'logged decision',update_decision:'updated decision',
  })[type] || type.replace(/_/g,' ');
}

function smartRepliesFor(text, actType) {
  const t = (text||'').toLowerCase();
  if (actType?.includes('task')||t.includes('task'))       return ['Add another','Show my tasks'];
  if (actType?.includes('workout')||t.includes('workout')) return ['Log another workout','Check my health'];
  if (actType?.includes('habit')||t.includes('habit'))     return ['Check my habits','Log a check-in'];
  if (actType?.includes('goal')||t.includes('goal'))       return ['How are my goals?','Update progress'];
  if (t.includes('calendar')||t.includes('event'))         return ["What's tomorrow?",'Add an event'];
  if (t.includes('finance')||t.includes('bill'))           return ['Show finances','Any bills due?'];
  return ["What should I focus on?",'Any alerts today?'];
}

// ── Message components ────────────────────────────────────────────────────────
function UserBubble({ msg }) {
  return (
    <div className={css.userRow}>
      <div className={css.userBubble}>{msg.text}</div>
    </div>
  );
}

function AgentBubble({ msg, onChipTap }) {
  return (
    <div className={css.agentRow}>
      <div className={css.agentAvatar}><MyloIcon size={28} /></div>
      <div className={css.agentContent}>
        {msg.text && (
          <div className={css.agentText}>
            {msg.text.split('\n').map((ln, i) => (
              <p key={i} style={{ margin: 0, minHeight: ln ? undefined : '0.4em' }}>{ln}</p>
            ))}
            {msg._streaming && <span className={css.cursor} />}
          </div>
        )}
        {(msg.cards || []).map((c, i) => (
          <div key={i} className={css.actionCard} data-kind={c.kind}>
            <div className={css.cardVerb}>{c.kind === 'capture' ? '· noted' : '✓ ' + c.verb}</div>
            <div className={css.cardLabel}>{c.label}</div>
          </div>
        ))}
        {(msg.chips || []).length > 0 && (
          <div className={css.disambigRow}>
            {msg.chips.map((c, i) => (
              <button key={i} className={css.disambigChip} onClick={() => onChipTap(c.fill)}>
                {c.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ChatPage({
  sysPrompt, anthropicKey: chatApiKey, setSt, notify, setTab,
  openVoice, pendingEmailActions, vinyl=[], events: events_=[], brief=[], sigs=[], st={},
}) {
  const [msgs,         setMsgs]         = useState([]);
  const [input,        setInput]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [isListening,  setIsListening]  = useState(false);
  const [smartReplies, setSmartReplies] = useState([]);

  const msgsRef = useRef(null);
  const taRef   = useRef(null);
  const recRef  = useRef(null);
  const sendRef = useRef(null);

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [msgs, loading]);

  const autoResize = e => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const startVoice = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { notify('Voice not supported in this browser'); return; }
    const rec = new SR();
    rec.continuous = false; rec.interimResults = true; rec.lang = 'en-US';
    recRef.current = rec;
    rec.onstart  = () => setIsListening(true);
    rec.onresult = e => {
      const t = Array.from(e.results).map(r => r[0].transcript).join('');
      setInput(t);
      if (taRef.current) {
        taRef.current.style.height = 'auto';
        taRef.current.style.height = Math.min(taRef.current.scrollHeight, 120) + 'px';
      }
    };
    rec.onend = () => {
      setIsListening(false);
      setTimeout(() => setInput(prev => {
        if (prev.trim()) { sendRef.current?.(prev); return ''; }
        return prev;
      }), 150);
    };
    rec.onerror = () => { setIsListening(false); notify("Couldn't hear — try again"); };
    rec.start();
  }, [notify]);

  const stopVoice = useCallback(() => { recRef.current?.stop(); setIsListening(false); }, []);

  const send = useCallback(async (override) => {
    const text = (override !== undefined ? override : input).trim();
    if (!text || loading) return;
    setInput(''); setSmartReplies([]);
    if (taRef.current) taRef.current.style.height = 'auto';

    const userMsg = { id: `u${Date.now()}`, role: 'user', text };
    setMsgs(p => [...p, userMsg]);
    setLoading(true);

    const history  = [...msgs, userMsg];
    const localKey = chatApiKey || (() => {
      try { return localStorage.getItem('mylo_api_key') || ''; } catch { return ''; }
    })();

    const MYLO_TOOLS = [
      { type: 'web_search_20250305', name: 'web_search' },
      { name: 'add_task', description: 'Add a task.', input_schema: { type: 'object', properties: { text: { type: 'string' }, bucket: { type: 'string', enum: ['today','thisWeek','later','waiting'] }, priority: { type: 'string', enum: ['high','medium','low'] }, dueDate: { type: 'string' } }, required: ['text','bucket'] } },
      { name: 'complete_task', description: 'Mark a task as done.', input_schema: { type: 'object', properties: { text: { type: 'string' } }, required: ['text'] } },
      { name: 'update_task', description: 'Update a task.', input_schema: { type: 'object', properties: { text: { type: 'string' }, bucket: { type: 'string', enum: ['today','thisWeek','later','waiting'] }, priority: { type: 'string', enum: ['high','medium','low'] }, dueDate: { type: 'string' } }, required: ['text'] } },
      { name: 'log_workout', description: 'Log a workout session.', input_schema: { type: 'object', properties: { type: { type: 'string' }, miles: { type: 'number' }, duration: { type: 'number' }, intensity: { type: 'string', enum: ['easy','moderate','hard','race'] }, note: { type: 'string' }, date: { type: 'string' } }, required: ['type'] } },
      { name: 'check_in_habit', description: 'Mark a habit as completed for today.', input_schema: { type: 'object', properties: { name: { type: 'string' } }, required: ['name'] } },
      { name: 'log_transaction', description: 'Log a financial transaction.', input_schema: { type: 'object', properties: { amount: { type: 'number' }, category: { type: 'string' }, note: { type: 'string' }, date: { type: 'string' } }, required: ['amount','category'] } },
      { name: 'add_expense', description: 'Add a recurring expense.', input_schema: { type: 'object', properties: { name: { type: 'string' }, amount: { type: 'number' }, freq: { type: 'string', enum: ['monthly','weekly','biweekly','annual','once'] }, cat: { type: 'string' }, next: { type: 'string' } }, required: ['name','amount','freq'] } },
      { name: 'add_goal', description: 'Create a new goal.', input_schema: { type: 'object', properties: { title: { type: 'string' }, cat: { type: 'string', enum: ['fitness','finance','work','personal','learning','health'] }, target: { type: 'string' }, targetDate: { type: 'string' }, note: { type: 'string' } }, required: ['title','cat'] } },
      { name: 'update_goal_progress', description: 'Update goal progress.', input_schema: { type: 'object', properties: { title: { type: 'string' }, progress: { type: 'number' } }, required: ['title','progress'] } },
      { name: 'add_vinyl', description: 'Add a vinyl record order.', input_schema: { type: 'object', properties: { artist: { type: 'string' }, album: { type: 'string' }, vendor: { type: 'string' }, eta: { type: 'string' }, price: { type: 'number' } }, required: ['artist','album'] } },
      { name: 'mark_vinyl_delivered', description: 'Mark vinyl as delivered.', input_schema: { type: 'object', properties: { artist: { type: 'string' } }, required: ['artist'] } },
      { name: 'add_event', description: 'Add a calendar event.', input_schema: { type: 'object', properties: { title: { type: 'string' }, date: { type: 'string' }, time: { type: 'string' }, note: { type: 'string' } }, required: ['title','date'] } },
      { name: 'add_reminder', description: 'Set a reminder.', input_schema: { type: 'object', properties: { message: { type: 'string' }, date: { type: 'string' }, time: { type: 'string' }, repeat: { type: 'string', enum: ['none','daily','weekly','monthly'] }, category: { type: 'string', enum: ['health','finance','work','personal','general'] } }, required: ['message','date'] } },
      { name: 'add_memory', description: 'Store a persistent fact about the user.', input_schema: { type: 'object', properties: { key: { type: 'string' }, cat: { type: 'string', enum: ['work','fitness','finance','immigration','personal','contacts','vinyl','health','decision','insight','reflection'] }, text: { type: 'string' } }, required: ['key','cat','text'] } },
      { name: 'capture_note', description: 'Capture a thought or idea.', input_schema: { type: 'object', properties: { text: { type: 'string' }, tags: { type: 'array', items: { type: 'string' } } }, required: ['text'] } },
      { name: 'set_tab', description: 'Navigate to a page in the app.', input_schema: { type: 'object', properties: { tab: { type: 'string', enum: ['dashboard','tasks','calendar','finance','health','goals','vinyl','ask','inbox','weekly','settings','contacts','reminders'] } }, required: ['tab'] } },
    ];

    const payload = {
      model: 'claude-sonnet-4-20250514',
      max_tokens: 1024,
      stream: true,
      tools: MYLO_TOOLS,
      tool_choice: { type: 'auto' },
      system: sysPrompt,

      messages: history.map(m => ({
        role: m.role === 'user' ? 'user' : 'assistant',
        content: m.toolResult
          ? [{ type: 'tool_result', tool_use_id: m.toolUseId, content: m.toolResult }]
          : m.toolUse
          ? [{ type: 'tool_use', id: m.toolUseId, name: m.toolUseName, input: m.toolUseInput }]
          : m.text || '...',
      })),
    };

    const pid = `a${Date.now()}`;
    setMsgs(p => [...p, { id: pid, role: 'agent', text: '', _streaming: true, cards: [], chips: [] }]);

    try {
      const endpoint = localKey ? 'https://api.anthropic.com/v1/messages' : '/api/chat';
      const headers  = localKey
        ? { 'Content-Type': 'application/json', 'x-api-key': localKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' }
        : { 'Content-Type': 'application/json' };

      const res = await fetch(endpoint, { method: 'POST', headers, body: JSON.stringify(payload) });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error?.message || `HTTP ${res.status}`); }

      const reader = res.body.getReader();
      const dec    = new TextDecoder();
      let buf = '', fullText = '';
      // Tool use state — accumulate input JSON across deltas
      let toolName = null, toolUseId = null, toolInputBuf = '';
      const cards = [], chips = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n'); buf = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') continue;
          try {
            const ev = JSON.parse(data);

            // Text streaming
            if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') {
              fullText += ev.delta.text;
              setMsgs(p => p.map(m => m.id === pid ? { ...m, text: fullText } : m));
            }

            // Tool use — block start (captures name + id)
            if (ev.type === 'content_block_start' && ev.content_block?.type === 'tool_use') {
              toolName = ev.content_block.name;
              toolUseId = ev.content_block.id;
              toolInputBuf = '';
            }

            // Tool use — input JSON accumulates across deltas
            if (ev.type === 'content_block_delta' && ev.delta?.type === 'input_json_delta') {
              toolInputBuf += ev.delta.partial_json;
            }

            // Tool use — block complete, execute the tool
            if (ev.type === 'content_block_stop' && toolName) {
              let input = {};
              try { input = JSON.parse(toolInputBuf); } catch {}
              const label = executeToolCall(toolName, input, setSt, setTab, notify, TODAY);
              if (label) {
                cards.push({ kind: 'action', verb: verbFor(toolName), label });
              }
              toolName = null; toolUseId = null; toolInputBuf = '';
            }

            // Stop reason — end of turn
            if (ev.type === 'message_stop') break;
          } catch {}
        }
      }

      setMsgs(p => p.map(m => m.id === pid
        ? { ...m, text: fullText || (cards.length ? '' : '...'), _streaming: false, cards, chips }
        : m
      ));
      if (cards.length === 0) setSmartReplies(smartRepliesFor(fullText, null));

    } catch (err) {
      const isKeyErr = err.message.includes('401');
      const isServerErr = !localKey && (err.message.includes('500') || err.message.includes('502') || err.message.includes('Server not configured'));
      const errText = isKeyErr
        ? 'API key invalid — check Settings.'
        : isServerErr
        ? 'Server not configured — add ANTHROPIC_API_KEY to Vercel env vars.'
        : `Something went wrong: ${err.message}`;
      setMsgs(p => p.map(m => m.id === pid
        ? { ...m, text: errText, _streaming: false }
        : m
      ));
    }
    setLoading(false);
  }, [msgs, input, loading, sysPrompt, setSt, notify, setTab, chatApiKey]);

  sendRef.current = send;

  const isEmpty      = msgs.length === 0;
  const lastHasChips = !isEmpty && (msgs[msgs.length - 1]?.chips || []).length > 0;
  const hasInput     = input.trim().length > 0;

  return (
    <div className={css.shell}>
      <div className={css.msgs} ref={msgsRef}>
        {isEmpty ? (
          <div className={css.empty}>
            {/* Top-right date/time stamp */}
            <DateStamp />
            {/* Centered content cluster */}
            <div className={css.emptyCluster}>
              <div className={css.emptyIcon}><MyloIcon size={64} /></div>
              <div className={css.emptyGreet}><GreetText name={st?.profile?.name} /></div>
              <div className={css.emptySub}>{(() => {
                const tasks = (st?.tasks||[]).filter(t => !t.done && t.bucket === 'today');
                const raceDate = st?.raceDate;
                const TODAY_STR = new Date().toISOString().slice(0,10);
                const raceDays = raceDate ? Math.round((new Date(raceDate+'T12:00:00') - new Date(TODAY_STR+'T12:00:00')) / 86400000) : null;
                const memories = st?.memories || [];
                if (raceDays !== null && raceDays >= 0 && raceDays <= 21)
                  return `race in ${raceDays}d · ${tasks.length} task${tasks.length !== 1 ? 's' : ''} today — what do you want to work on?`;
                if (tasks.length > 0)
                  return `${tasks.length} task${tasks.length !== 1 ? 's' : ''} on your list today — where do you want to start?`;
                if (memories.length > 0)
                  return `i remember ${memories.length} thing${memories.length !== 1 ? 's' : ''} about you — what's on your mind?`;
                return "tell me what's on your mind";
              })()}</div>
              {(st?.memories?.length > 0 || st?.raceDate || (st?.tasks||[]).length > 0) && (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center', marginBottom:8 }}>
                  {st?.raceDate && (() => {
                    const d = Math.round((new Date(st.raceDate+'T12:00:00') - new Date(new Date().toISOString().slice(0,10)+'T12:00:00')) / 86400000);
                    return d >= 0 ? <span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>race in {d}d</span> : null;
                  })()}
                  {(st?.vinyl||[]).length > 0 && <span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>{(st.vinyl||[]).filter(v=>v.status==='collection').length} records</span>}
                  {(st?.memories||[]).length > 0 && <span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>{(st.memories||[]).length} memor{(st.memories||[]).length === 1 ? 'y' : 'ies'}</span>}
                  {(st?.tasks||[]).filter(t=>!t.done&&t.bucket==='today').length > 0 && <span style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>{(st.tasks||[]).filter(t=>!t.done&&t.bucket==='today').length} tasks today</span>}
                </div>
              )}
              {(st?.memories?.length > 0 || st?.raceDate || (st?.tasks||[]).length > 0) && (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'center', marginBottom:8 }}>
                  {st?.raceDate && (() => {
                    const d = Math.round((new Date(st.raceDate+'T12:00:00') - new Date(new Date().toISOString().slice(0,10)+'T12:00:00')) / 86400000);
                    return d >= 0 ? <span key="race" style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>race in {d}d</span> : null;
                  })()}
                  {(st?.vinyl||[]).filter(v=>v.status==='collection').length > 0 && <span key="vinyl" style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>{(st.vinyl||[]).filter(v=>v.status==='collection').length} records</span>}
                  {(st?.memories||[]).length > 0 && <span key="mem" style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>{(st.memories||[]).length} {(st.memories||[]).length===1?'memory':'memories'}</span>}
                  {(st?.tasks||[]).filter(t=>!t.done&&t.bucket==='today').length > 0 && <span key="tasks" style={{ fontFamily:'var(--sans)', fontSize:10, color:'var(--t3)', background:'var(--s2)', border:'0.5px solid var(--b1)', borderRadius:'var(--r)', padding:'2px 6px' }}>{(st.tasks||[]).filter(t=>!t.done&&t.bucket==='today').length} tasks today</span>}
                </div>
              )}
              <div className={css.starterWrap}>
                <div className={css.starterRow}>
                  {buildStarters(st, sigs).slice(0, 3).map(c => (
                    <button
                      key={c.lbl}
                      className={[css.chip, c.primary ? css.chipPrimary : css.chipSecondary].join(' ')}
                      onClick={() => { setInput(c.pre); setTimeout(() => taRef.current?.focus(), 50); }}
                    >{c.lbl}</button>
                  ))}
                </div>
                <div className={css.starterRow}>
                  {buildStarters(st, sigs).slice(3).map(c => (
                    <button
                      key={c.lbl}
                      className={[css.chip, css.chipSecondary].join(' ')}
                      onClick={() => { setInput(c.pre); setTimeout(() => taRef.current?.focus(), 50); }}
                    >{c.lbl}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          msgs.map(m => m.role === 'user'
            ? <UserBubble key={m.id} msg={m} />
            : <AgentBubble key={m.id} msg={m}
                onChipTap={fill => { setInput(fill); setTimeout(() => taRef.current?.focus(), 50); }} />
          )
        )}
        {loading && !msgs.some(m => m._streaming && m.text) && (
          <div className={css.agentRow}>
            <div className={css.agentAvatar}><MyloIcon size={28} /></div>
            <div className={css.agentContent}><div className={css.pulse} /></div>
          </div>
        )}
      </div>

      <div className={css.inputArea}>
        {!isEmpty && !lastHasChips && smartReplies.length > 0 && (
          <div className={css.chipStrip}>
            {smartReplies.map((s, i) => (
              <button key={i} className={[css.chip, css.chipContext].join(' ')}
                onClick={() => { setInput(s); setTimeout(() => taRef.current?.focus(), 50); }}>
                {s}
              </button>
            ))}
          </div>
        )}
        <div className={css.bar}>
          <button
            className={[css.voiceBtn, isListening ? css.voiceActive : ''].filter(Boolean).join(' ')}
            onClick={isListening ? stopVoice : startVoice}
            aria-label={isListening ? 'Stop' : 'Voice input'}
          >
            {isListening ? <MicOff size={17} strokeWidth={1.5} /> : <Mic size={17} strokeWidth={1.5} />}
          </button>
          <textarea
            ref={taRef}
            className={css.ta}
            placeholder={isListening ? 'listening…' : "what's on your mind"}
            value={input} rows={1}
            onChange={e => { setInput(e.target.value); autoResize(e); }}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          />
          <button
            className={[css.sendBtn, hasInput && !loading ? css.sendActive : loading ? css.sendLoading : ''].filter(Boolean).join(' ')}
            onClick={() => send()}
            disabled={!hasInput || loading}
            aria-label="Send"
          >
            <ArrowUp size={16} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
